import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Sun, 
  Battery, 
  Save, 
  Plus, 
  Loader2, 
  AlertCircle,
  Settings,
  ArrowLeft,
  Lock,
  Mail,
  Eye,
  EyeOff,
  LogOut,
  Tag,
  Users,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Calendar,
  Phone,
  MapPin,
  Clock,
  ExternalLink,
  TrendingUp,
  Activity,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Sparkles } from 'lucide-react';
import { BlogAiStudio } from '../components/admin/BlogAiStudio';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Login Component
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        username,
        password
      });

      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        toast.success('Login successful!');
        onLogin(response.data.token);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Invalid username or password');
      toast.error('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-cyo-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-cyo-orange" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">Admin Login</CardTitle>
          <p className="text-gray-500 text-sm mt-1">Enter your credentials to access the admin portal</p>
        </CardHeader>
        
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                Email
              </label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-12"
                disabled={isLoading}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Lock className="w-4 h-4 text-gray-400" />
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-10"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-cyo-orange hover:bg-cyo-orange-dark text-white font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-gray-500 hover:text-cyo-orange flex items-center justify-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Lead Status options and helpers
const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'qualified', label: 'Qualified', color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  { value: 'quoted', label: 'Quoted', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { value: 'won', label: 'Won', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700 border-red-300' },
];

const getLeadStatusColor = (value) => {
  const opt = LEAD_STATUS_OPTIONS.find(o => o.value === value);
  return opt ? opt.color : 'bg-gray-100 text-gray-700 border-gray-300';
};

const getLeadStatusLabel = (value) => {
  const opt = LEAD_STATUS_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : (value || 'New');
};

// Lead Detail Modal
const LeadDetailModal = ({ lead, onClose, onLeadUpdated }) => {
  const [leadStatus, setLeadStatus] = useState(lead?.lead_status || 'new');
  const [followUpDate, setFollowUpDate] = useState(lead?.follow_up_date || '');
  const [notes, setNotes] = useState(lead?.internal_notes || []);
  const [newNoteText, setNewNoteText] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);

  // Re-sync local state when a different lead is opened
  useEffect(() => {
    setLeadStatus(lead?.lead_status || 'new');
    setFollowUpDate(lead?.follow_up_date || '');
    setNotes(lead?.internal_notes || []);
    setNewNoteText('');
  }, [lead?.id]);

  const handleStatusChange = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/enquiries/${lead.id}/lead-status`, {
        lead_status: newStatus
      });
      setLeadStatus(newStatus);
      toast.success(`Lead status updated to ${getLeadStatusLabel(newStatus)}`);
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      console.error('Failed to update lead status:', err);
      toast.error('Failed to update lead status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleFollowUpSave = async () => {
    setIsUpdatingDate(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/enquiries/${lead.id}/follow-up`, {
        follow_up_date: followUpDate || null
      });
      toast.success(followUpDate ? 'Follow-up date saved' : 'Follow-up date cleared');
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      console.error('Failed to update follow-up date:', err);
      toast.error('Failed to update follow-up date');
    } finally {
      setIsUpdatingDate(false);
    }
  };

  const handleAddNote = async () => {
    const text = newNoteText.trim();
    if (!text) {
      toast.error('Please enter a note');
      return;
    }
    setIsAddingNote(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/enquiries/${lead.id}/notes`, {
        text
      });
      setNotes(prev => [...prev, res.data.note]);
      setNewNoteText('');
      toast.success('Note added');
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error('Failed to add note');
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/enquiries/${lead.id}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Note deleted');
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      console.error('Failed to delete note:', err);
      toast.error('Failed to delete note');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProductLabel = (type) => {
    const labels = {
      'solar': 'Solar Systems',
      'battery': 'Battery Storage',
      'ev-charger': 'EV Chargers',
      'heat-pump': 'Heat Pumps',
      'hvac': 'Heating & Cooling'
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Lead Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Lead Status Management */}
          <div data-testid="lead-status-section">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Lead Status</h3>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {LEAD_STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    data-testid={`lead-status-${opt.value}-btn`}
                    onClick={() => handleStatusChange(opt.value)}
                    disabled={isUpdatingStatus}
                    className={`text-xs font-medium px-2 py-2 rounded-lg border transition-all ${
                      leadStatus === opt.value
                        ? `${opt.color} border-2 ring-2 ring-offset-1 ring-cyo-orange/30`
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                    } ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Follow-up Date */}
          <div data-testid="follow-up-section">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Follow-up Date</h3>
            <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <Input
                data-testid="follow-up-date-input"
                type="date"
                value={followUpDate || ''}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="flex-1"
              />
              <Button
                data-testid="follow-up-save-btn"
                onClick={handleFollowUpSave}
                disabled={isUpdatingDate}
                className="bg-cyo-orange hover:bg-cyo-orange-dark text-white"
              >
                {isUpdatingDate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
              {followUpDate && (
                <Button
                  data-testid="follow-up-clear-btn"
                  onClick={() => { setFollowUpDate(''); }}
                  variant="outline"
                  size="sm"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Internal Notes */}
          <div data-testid="notes-section">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Internal Notes ({notes.length})</h3>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {notes.length === 0 ? (
                  <p className="text-sm text-gray-400 italic text-center py-4">No notes yet</p>
                ) : (
                  notes.map(note => (
                    <div key={note.id} data-testid={`note-${note.id}`} className="bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap flex-1">{note.text}</p>
                        <button
                          data-testid={`delete-note-${note.id}-btn`}
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-gray-400 hover:text-red-500 flex-shrink-0"
                          title="Delete note"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {note.created_by || 'admin'} • {formatDate(note.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </div>
              <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
                <textarea
                  data-testid="new-note-textarea"
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  placeholder="Add an internal note (not visible to customer)..."
                  className="w-full min-h-[70px] p-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyo-orange/20 resize-y"
                />
                <Button
                  data-testid="add-note-btn"
                  onClick={handleAddNote}
                  disabled={isAddingNote || !newNoteText.trim()}
                  className="bg-cyo-orange hover:bg-cyo-orange-dark text-white self-end"
                  size="sm"
                >
                  {isAddingNote ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Add Note
                </Button>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Contact Information</h3>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-cyo-orange" />
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-medium text-gray-900">
                    {lead.first_name && lead.last_name 
                      ? `${lead.first_name} ${lead.last_name}` 
                      : 'Not provided'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-cyo-orange" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{lead.email || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-cyo-orange" />
                <div>
                  <p className="text-xs text-gray-500">Mobile</p>
                  <p className="font-medium text-gray-900">{lead.mobile || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-cyo-orange" />
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  {lead.address ? (
                    <>
                      <p className="font-medium text-gray-900 text-sm">{lead.address}</p>
                      {lead.postcode && <p className="text-xs text-gray-500">Postcode: {lead.postcode}</p>}
                    </>
                  ) : (
                    <p className="font-medium text-gray-900">{lead.postcode || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Product Interest */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Product Interest</h3>
            <div className="bg-orange-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Product Type</span>
                <span className="font-semibold text-cyo-orange">{getProductLabel(lead.product_type)}</span>
              </div>
              {lead.monthly_bill && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Monthly Bill</span>
                  <span className="font-medium">${lead.monthly_bill}</span>
                </div>
              )}
              {lead.solar_system_size && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Solar System Size</span>
                  <span className="font-medium">{lead.solar_system_size}</span>
                </div>
              )}
              {lead.solar_years && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Solar Years</span>
                  <span className="font-medium">{lead.solar_years}</span>
                </div>
              )}
            </div>
          </div>

          {/* Selected Option */}
          {(lead.selected_tier || lead.selected_option_name) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Selected Option</h3>
              <div className="bg-green-50 rounded-xl p-4 space-y-2">
                {lead.selected_tier && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Tier</span>
                    <span className="font-medium text-green-700">{lead.selected_tier}</span>
                  </div>
                )}
                {lead.selected_option_name && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Option</span>
                    <span className="font-medium">{lead.selected_option_name}</span>
                  </div>
                )}
                {lead.selected_system_size && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">System Size</span>
                    <span className="font-medium">{lead.selected_system_size}</span>
                  </div>
                )}
                {lead.selected_annual_savings && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Est. Annual Savings</span>
                    <span className="font-semibold text-green-600">${lead.selected_annual_savings.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Timeline</h3>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Journey Started</p>
                  <p className="font-medium text-gray-900">{formatDate(lead.journey_started_at || lead.created_at)}</p>
                </div>
              </div>
              {lead.last_activity_at && (
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Last Activity</p>
                    <p className="font-medium text-gray-900">{formatDate(lead.last_activity_at)}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    lead.status === 'contacted' ? 'bg-green-100 text-green-700' :
                    lead.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {lead.status || 'pending'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Journey Tracking */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Customer Journey</h3>
            <div className={`rounded-xl p-4 ${
              lead.journey_status === 'completed' ? 'bg-green-50' :
              lead.journey_status === 'abandoned' ? 'bg-red-50' :
              'bg-orange-50'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">Journey Status</span>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                  lead.journey_status === 'completed' ? 'bg-green-100 text-green-700' :
                  lead.journey_status === 'abandoned' ? 'bg-red-100 text-red-700' :
                  'bg-orange-100 text-orange-700'
                }`}>
                  {lead.journey_status === 'completed' ? '✓ Completed' :
                   lead.journey_status === 'abandoned' ? '✗ Abandoned' :
                   '⏳ In Progress'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last Step Reached</span>
                <span className="font-medium text-gray-900">
                  {getStepLabel(lead.last_step)}
                </span>
              </div>
            </div>
          </div>

          {/* Lead ID */}
          <div className="text-center text-xs text-gray-400">
            Lead ID: {lead.id}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get step labels
const getStepLabel = (step) => {
  const labels = {
    'started': '1. Started',
    'address_entered': '2. Entered Address',
    'details_entered': '3. Entered Details',
    'results_viewed': '4. Viewed Results',
    'otp_sent': '5. OTP Sent',
    'otp_verified': '6. OTP Verified',
    'contact_entered': '7. Entered Contact',
    'completed': '8. Completed ✓'
  };
  return labels[step] || step || 'Unknown';
};

// Admin Dashboard Component
const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('leads');
  const [solarPricing, setSolarPricing] = useState([]);
  const [batteryPricing, setBatteryPricing] = useState([]);
  const [leads, setLeads] = useState([]);
  const [journeyAnalytics, setJourneyAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Pricing form states
  const [newSolarSize, setNewSolarSize] = useState('');
  const [newSolarWasPrice, setNewSolarWasPrice] = useState('');
  const [newSolarNowPrice, setNewSolarNowPrice] = useState('');
  const [newBatterySize, setNewBatterySize] = useState('');
  const [newBatteryWasPrice, setNewBatteryWasPrice] = useState('');
  const [newBatteryNowPrice, setNewBatteryNowPrice] = useState('');
  
  // Lead filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [leadStatusFilter, setLeadStatusFilter] = useState('all');
  const [journeyFilter, setJourneyFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch pricing
      const pricingResponse = await axios.get(`${BACKEND_URL}/api/admin/pricing`);
      const solarData = (pricingResponse.data.solar_pricing || []).map(item => ({
        size_kw: item.size_kw,
        was_price: item.was_price || (item.price ? item.price + 2000 : 0),
        now_price: item.now_price || item.price || 0
      }));
      const batteryData = (pricingResponse.data.battery_pricing || []).map(item => ({
        size_kwh: item.size_kwh,
        was_price: item.was_price || (item.price ? item.price + 2000 : 0),
        now_price: item.now_price || item.price || 0
      }));
      setSolarPricing(solarData);
      setBatteryPricing(batteryData);

      // Fetch leads
      const leadsResponse = await axios.get(`${BACKEND_URL}/api/enquiries`);
      setLeads(leadsResponse.data.enquiries || []);

      // Fetch journey analytics
      try {
        const journeyResponse = await axios.get(`${BACKEND_URL}/api/enquiries/journey-analytics`);
        setJourneyAnalytics(journeyResponse.data);
      } catch (journeyErr) {
        console.error('Error fetching journey analytics:', journeyErr);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Pricing handlers
  const handleSolarWasPriceChange = (index, value) => {
    const updated = [...solarPricing];
    updated[index].was_price = parseInt(value) || 0;
    setSolarPricing(updated);
  };

  const handleSolarNowPriceChange = (index, value) => {
    const updated = [...solarPricing];
    updated[index].now_price = parseInt(value) || 0;
    setSolarPricing(updated);
  };

  const handleBatteryWasPriceChange = (index, value) => {
    const updated = [...batteryPricing];
    updated[index].was_price = parseInt(value) || 0;
    setBatteryPricing(updated);
  };

  const handleBatteryNowPriceChange = (index, value) => {
    const updated = [...batteryPricing];
    updated[index].now_price = parseInt(value) || 0;
    setBatteryPricing(updated);
  };

  const saveSolarPricing = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/pricing/solar`, solarPricing);
      toast.success('Solar pricing saved successfully!');
    } catch (err) {
      console.error('Error saving solar pricing:', err);
      toast.error('Failed to save solar pricing');
    } finally {
      setIsSaving(false);
    }
  };

  const saveBatteryPricing = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/pricing/battery`, batteryPricing);
      toast.success('Battery pricing saved successfully!');
    } catch (err) {
      console.error('Error saving battery pricing:', err);
      toast.error('Failed to save battery pricing');
    } finally {
      setIsSaving(false);
    }
  };

  const addSolarSize = async () => {
    if (!newSolarSize || !newSolarWasPrice || !newSolarNowPrice) {
      toast.error('Please enter size, Was price, and Now price');
      return;
    }
    const size = parseInt(newSolarSize);
    const wasPrice = parseInt(newSolarWasPrice);
    const nowPrice = parseInt(newSolarNowPrice);

    if (solarPricing.some(p => p.size_kw === size)) {
      toast.error(`Solar size ${size}kW already exists`);
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/admin/pricing/solar/add`, {
        size_kw: size, was_price: wasPrice, now_price: nowPrice
      });
      const updated = [...solarPricing, { size_kw: size, was_price: wasPrice, now_price: nowPrice }];
      updated.sort((a, b) => a.size_kw - b.size_kw);
      setSolarPricing(updated);
      setNewSolarSize('');
      setNewSolarWasPrice('');
      setNewSolarNowPrice('');
      toast.success(`Added ${size}kW solar system`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add solar size');
    }
  };

  const addBatterySize = async () => {
    if (!newBatterySize || !newBatteryWasPrice || !newBatteryNowPrice) {
      toast.error('Please enter size, Was price, and Now price');
      return;
    }
    const size = parseInt(newBatterySize);
    const wasPrice = parseInt(newBatteryWasPrice);
    const nowPrice = parseInt(newBatteryNowPrice);

    if (batteryPricing.some(p => p.size_kwh === size)) {
      toast.error(`Battery size ${size}kWh already exists`);
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/admin/pricing/battery/add`, {
        size_kwh: size, was_price: wasPrice, now_price: nowPrice
      });
      const updated = [...batteryPricing, { size_kwh: size, was_price: wasPrice, now_price: nowPrice }];
      updated.sort((a, b) => a.size_kwh - b.size_kwh);
      setBatteryPricing(updated);
      setNewBatterySize('');
      setNewBatteryWasPrice('');
      setNewBatteryNowPrice('');
      toast.success(`Added ${size}kWh battery system`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add battery size');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout?token=${token}`);
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('adminToken');
    onLogout();
    toast.success('Logged out successfully');
  };

  // Filter and sort leads
  const filteredLeads = leads
    .filter(lead => {
      const matchesSearch = searchTerm === '' || 
        (lead.first_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.last_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.mobile?.includes(searchTerm)) ||
        (lead.postcode?.includes(searchTerm)) ||
        (lead.address?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesProduct = productFilter === 'all' || lead.product_type === productFilter;
      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
      const matchesLeadStatus = leadStatusFilter === 'all' || (lead.lead_status || 'new') === leadStatusFilter;
      const matchesJourney = journeyFilter === 'all' || lead.journey_status === journeyFilter;
      
      return matchesSearch && matchesProduct && matchesStatus && matchesLeadStatus && matchesJourney;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  const getProductLabel = (type) => {
    const labels = {
      'solar': 'Solar',
      'battery': 'Battery',
      'ev-charger': 'EV Charger',
      'heat-pump': 'Heat Pump',
      'hvac': 'HVAC'
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-cyo-orange mx-auto mb-4" />
          <p className="text-gray-500">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </a>
              <div className="flex items-center gap-2">
                <Settings className="w-6 h-6 text-cyo-orange" />
                <h1 className="text-xl font-bold text-gray-900">Admin Portal</h1>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="text-gray-600 hover:text-red-600 hover:border-red-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('leads')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'leads'
                  ? 'border-cyo-orange text-cyo-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Leads ({leads.length})
            </button>
            <button
              onClick={() => setActiveTab('journey')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'journey'
                  ? 'border-cyo-orange text-cyo-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Journey Analytics
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'pricing'
                  ? 'border-cyo-orange text-cyo-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Tag className="w-4 h-4 inline mr-2" />
              Pricing Offers
            </button>
            <button
              onClick={() => setActiveTab('blog')}
              data-testid="admin-tab-blog"
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'blog'
                  ? 'border-cyo-orange text-cyo-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              Blog AI Studio
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div>
            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-wrap gap-4 items-center">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, email, mobile, postcode..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Product Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <select
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                    className="h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyo-orange/20"
                  >
                    <option value="all">All Products</option>
                    <option value="solar">Solar</option>
                    <option value="battery">Battery</option>
                    <option value="ev-charger">EV Charger</option>
                    <option value="heat-pump">Heat Pump</option>
                    <option value="hvac">HVAC</option>
                  </select>
                </div>

                {/* Status Filter */}
                <select
                  data-testid="status-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyo-orange/20"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="completed">Completed</option>
                </select>

                {/* Lead Status Filter */}
                <select
                  data-testid="lead-status-filter-select"
                  value={leadStatusFilter}
                  onChange={(e) => setLeadStatusFilter(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyo-orange/20"
                >
                  <option value="all">All Lead Status</option>
                  {LEAD_STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                {/* Journey Filter */}
                <select
                  value={journeyFilter}
                  onChange={(e) => setJourneyFilter(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyo-orange/20"
                >
                  <option value="all">All Journeys</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="abandoned">Abandoned</option>
                </select>

                {/* Sort Order */}
                <button
                  onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                  className="h-10 px-3 rounded-lg border border-gray-200 text-sm flex items-center gap-2 hover:bg-gray-50"
                >
                  {sortOrder === 'newest' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
                </button>
              </div>
            </div>

            {/* Leads Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date/Time</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Journey</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Lead Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Follow-up</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredLeads.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                          {leads.length === 0 ? 'No leads yet' : 'No leads match your filters'}
                        </td>
                      </tr>
                    ) : (
                      filteredLeads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{formatDate(lead.created_at)}</div>
                            <div className="text-xs text-gray-500">{formatTime(lead.created_at)}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.first_name && lead.last_name 
                                ? `${lead.first_name} ${lead.last_name}` 
                                : <span className="text-gray-400 italic">Not provided</span>}
                            </div>
                            <div className="text-xs text-gray-500">{lead.email || lead.mobile || '-'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              lead.product_type === 'solar' ? 'bg-amber-100 text-amber-700' :
                              lead.product_type === 'battery' ? 'bg-blue-100 text-blue-700' :
                              lead.product_type === 'ev-charger' ? 'bg-green-100 text-green-700' :
                              lead.product_type === 'heat-pump' ? 'bg-red-100 text-red-700' :
                              'bg-cyan-100 text-cyan-700'
                            }`}>
                              {getProductLabel(lead.product_type)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">
                              {lead.address ? (
                                <span className="truncate max-w-[150px] block" title={lead.address}>
                                  {lead.address.length > 25 ? lead.address.substring(0, 25) + '...' : lead.address}
                                </span>
                              ) : (
                                lead.postcode || '-'
                              )}
                            </div>
                            {lead.address && lead.postcode && (
                              <div className="text-xs text-gray-500">{lead.postcode}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                lead.journey_status === 'completed' ? 'bg-green-100 text-green-700' :
                                lead.journey_status === 'abandoned' ? 'bg-red-100 text-red-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {lead.journey_status === 'completed' ? '✓ Done' :
                                 lead.journey_status === 'abandoned' ? '✗ Left' :
                                 '⏳ Active'}
                              </span>
                              <span className="text-xs text-gray-500 truncate max-w-[100px]" title={getStepLabel(lead.last_step)}>
                                {lead.last_step ? getStepLabel(lead.last_step).split('. ')[1] || lead.last_step : '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              data-testid={`lead-status-badge-${lead.id}`}
                              className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getLeadStatusColor(lead.lead_status || 'new')}`}
                            >
                              {getLeadStatusLabel(lead.lead_status || 'new')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {lead.follow_up_date ? (
                              <div className="flex items-center gap-1 text-sm">
                                <Calendar className={`w-4 h-4 ${
                                  new Date(lead.follow_up_date) < new Date(new Date().toDateString())
                                    ? 'text-red-500' : 'text-cyo-orange'
                                }`} />
                                <span className={
                                  new Date(lead.follow_up_date) < new Date(new Date().toDateString())
                                    ? 'text-red-600 font-medium' : 'text-gray-700'
                                }>
                                  {new Date(lead.follow_up_date).toLocaleDateString('en-AU', {
                                    day: '2-digit', month: 'short'
                                  })}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              data-testid={`view-lead-${lead.id}-btn`}
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedLead(lead)}
                              className="text-cyo-orange hover:text-cyo-orange-dark hover:bg-orange-50"
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Table Footer */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
                Showing {filteredLeads.length} of {leads.length} leads
              </div>
            </div>
          </div>
        )}

        {/* Journey Analytics Tab */}
        {activeTab === 'journey' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Total Journeys</p>
                      <p className="text-3xl font-bold">{journeyAnalytics?.total_journeys || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Completed</p>
                      <p className="text-3xl font-bold">{journeyAnalytics?.by_status?.completed || 0}</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">In Progress</p>
                      <p className="text-3xl font-bold">{journeyAnalytics?.by_status?.in_progress || 0}</p>
                    </div>
                    <Activity className="w-10 h-10 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Completion Rate</p>
                      <p className="text-3xl font-bold">{journeyAnalytics?.completion_rate || 0}%</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Funnel Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyo-orange" />
                  Customer Journey Funnel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-6">
                  See where customers drop off in their journey. Higher numbers at earlier stages indicate areas for improvement.
                </p>
                
                <div className="space-y-3">
                  {journeyAnalytics?.step_labels && Object.entries(journeyAnalytics.step_labels).map(([step, label], index) => {
                    const count = journeyAnalytics?.by_step?.[step] || 0;
                    const total = journeyAnalytics?.total_journeys || 1;
                    const percentage = Math.round((count / total) * 100);
                    const isDropOff = index > 0 && index < 7 && count > 0;
                    
                    return (
                      <div key={step} className="flex items-center gap-4">
                        <div className="w-32 text-sm font-medium text-gray-700">
                          {label}
                        </div>
                        <div className="flex-1">
                          <div className="h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                            <div 
                              className={`h-full transition-all duration-500 ${
                                step === 'completed' ? 'bg-green-500' :
                                isDropOff ? 'bg-orange-400' : 'bg-blue-400'
                              }`}
                              style={{ width: `${Math.max(percentage, 2)}%` }}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-700">
                              {count} users ({percentage}%)
                            </span>
                          </div>
                        </div>
                        {isDropOff && count > 0 && (
                          <AlertTriangle className="w-5 h-5 text-orange-500" title="Potential drop-off point" />
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {journeyAnalytics?.likely_abandoned > 0 && (
                  <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">
                        {journeyAnalytics.likely_abandoned} customers may have abandoned their journey (inactive for 30+ minutes)
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Drop-off Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Drop-off Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Common Drop-off Points</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      {journeyAnalytics?.by_step && (
                        <>
                          {(journeyAnalytics.by_step.details_entered || 0) > 0 && (
                            <li className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-orange-400" />
                              <span><strong>{journeyAnalytics.by_step.details_entered}</strong> stopped after entering details</span>
                            </li>
                          )}
                          {(journeyAnalytics.by_step.results_viewed || 0) > 0 && (
                            <li className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-orange-400" />
                              <span><strong>{journeyAnalytics.by_step.results_viewed}</strong> viewed results but didn't continue</span>
                            </li>
                          )}
                          {(journeyAnalytics.by_step.otp_sent || 0) > 0 && (
                            <li className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-orange-400" />
                              <span><strong>{journeyAnalytics.by_step.otp_sent}</strong> received OTP but didn't verify</span>
                            </li>
                          )}
                        </>
                      )}
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Recommendations</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-400 mt-1.5" />
                        <span>Consider simplifying the OTP step or adding "call me instead" option</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-400 mt-1.5" />
                        <span>Add exit-intent popups to capture leads before they leave</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-400 mt-1.5" />
                        <span>Follow up with in-progress leads via email or SMS</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div>
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Solar Pricing Card */}
              <Card className="shadow-lg">
                <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <Sun className="w-6 h-6" />
                    Solar System Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-sm text-gray-500 mb-4">
                    Enter "Was" (original) and "Now" (sale) prices for each solar system size
                  </p>
                  
                  <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto pr-2">
                    <div className="grid grid-cols-4 gap-2 text-xs font-medium text-gray-500 pb-2 border-b sticky top-0 bg-white">
                      <span>Size</span>
                      <span className="text-gray-400">Was Price</span>
                      <span className="text-green-600">Now Price</span>
                      <span className="text-right">Savings</span>
                    </div>
                    
                    {solarPricing.map((item, index) => (
                      <div key={item.size_kw} className="grid grid-cols-4 gap-2 items-center">
                        <div className="flex items-center gap-1">
                          <Sun className="w-3 h-3 text-amber-500" />
                          <span className="font-medium text-sm">{item.size_kw}kW</span>
                        </div>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <Input
                            type="number"
                            value={item.was_price}
                            onChange={(e) => handleSolarWasPriceChange(index, e.target.value)}
                            className="pl-5 h-9 text-sm text-gray-400"
                          />
                        </div>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-green-500 text-xs">$</span>
                          <Input
                            type="number"
                            value={item.now_price}
                            onChange={(e) => handleSolarNowPriceChange(index, e.target.value)}
                            className="pl-5 h-9 text-sm text-green-600 font-medium border-green-200"
                          />
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                            -${(item.was_price - item.now_price).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4 mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Add New Size</p>
                    <div className="grid grid-cols-4 gap-2">
                      <Input type="number" placeholder="kW" value={newSolarSize} onChange={(e) => setNewSolarSize(e.target.value)} className="h-9 text-sm" />
                      <Input type="number" placeholder="Was $" value={newSolarWasPrice} onChange={(e) => setNewSolarWasPrice(e.target.value)} className="h-9 text-sm" />
                      <Input type="number" placeholder="Now $" value={newSolarNowPrice} onChange={(e) => setNewSolarNowPrice(e.target.value)} className="h-9 text-sm" />
                      <Button onClick={addSolarSize} variant="outline" size="sm" className="h-9"><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>

                  <Button onClick={saveSolarPricing} className="w-full bg-amber-500 hover:bg-amber-600 text-white" disabled={isSaving}>
                    {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Solar Pricing
                  </Button>
                </CardContent>
              </Card>

              {/* Battery Pricing Card */}
              <Card className="shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <Battery className="w-6 h-6" />
                    Battery System Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-sm text-gray-500 mb-4">
                    Enter "Was" (original) and "Now" (sale) prices for each battery system size
                  </p>
                  
                  <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto pr-2">
                    <div className="grid grid-cols-4 gap-2 text-xs font-medium text-gray-500 pb-2 border-b sticky top-0 bg-white">
                      <span>Size</span>
                      <span className="text-gray-400">Was Price</span>
                      <span className="text-green-600">Now Price</span>
                      <span className="text-right">Savings</span>
                    </div>
                    
                    {batteryPricing.map((item, index) => (
                      <div key={item.size_kwh} className="grid grid-cols-4 gap-2 items-center">
                        <div className="flex items-center gap-1">
                          <Battery className="w-3 h-3 text-blue-500" />
                          <span className="font-medium text-sm">{item.size_kwh}kWh</span>
                        </div>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <Input
                            type="number"
                            value={item.was_price}
                            onChange={(e) => handleBatteryWasPriceChange(index, e.target.value)}
                            className="pl-5 h-9 text-sm text-gray-400"
                          />
                        </div>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-green-500 text-xs">$</span>
                          <Input
                            type="number"
                            value={item.now_price}
                            onChange={(e) => handleBatteryNowPriceChange(index, e.target.value)}
                            className="pl-5 h-9 text-sm text-green-600 font-medium border-green-200"
                          />
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                            -${(item.was_price - item.now_price).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4 mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Add New Size</p>
                    <div className="grid grid-cols-4 gap-2">
                      <Input type="number" placeholder="kWh" value={newBatterySize} onChange={(e) => setNewBatterySize(e.target.value)} className="h-9 text-sm" />
                      <Input type="number" placeholder="Was $" value={newBatteryWasPrice} onChange={(e) => setNewBatteryWasPrice(e.target.value)} className="h-9 text-sm" />
                      <Input type="number" placeholder="Now $" value={newBatteryNowPrice} onChange={(e) => setNewBatteryNowPrice(e.target.value)} className="h-9 text-sm" />
                      <Button onClick={addBatterySize} variant="outline" size="sm" className="h-9"><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>

                  <Button onClick={saveBatteryPricing} className="w-full bg-blue-500 hover:bg-blue-600 text-white" disabled={isSaving}>
                    {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Battery Pricing
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Info Box */}
            <div className="mt-8 bg-blue-50 border border-blue-100 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Tag className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">How Sale Pricing Works</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>Was Price</strong>: The original/higher price shown with strikethrough</li>
                    <li>• <strong>Now Price</strong>: The current sale price shown in green</li>
                    <li>• <strong>Savings</strong>: Automatically calculated as (Was - Now)</li>
                    <li>• Customers see both prices with the savings prominently displayed</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog AI Studio Tab */}
        {activeTab === 'blog' && <BlogAiStudio token={token} />}
      </main>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onLeadUpdated={fetchData}
        />
      )}
    </div>
  );
};

// Main Admin Page Component
const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    const verifySession = async () => {
      const storedToken = localStorage.getItem('adminToken');
      
      if (storedToken) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/admin/verify?token=${storedToken}`);
          if (response.data.valid) {
            setToken(storedToken);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('adminToken');
          }
        } catch (err) {
          console.error('Session verification failed:', err);
          localStorage.removeItem('adminToken');
        }
      }
      
      setIsVerifying(false);
    };

    verifySession();
  }, []);

  const handleLogin = (newToken) => {
    setToken(newToken);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setToken(null);
    setIsAuthenticated(false);
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-cyo-orange mx-auto mb-4" />
          <p className="text-gray-500">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
};

export default AdminPage;
